import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { CODLogoMark, CODLogoSVG, ChicagoStars, SkylineDivider } from "../components/cod/Logo";import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { IMG } from "../lib/images";
import { Seo } from "../components/cod/Seo";
import { PUBLIC_SIGNUP_ROLES, portalForUser } from "../lib/roles";

const SidePanel = ({ heading, sub }) => (
  <div className="hidden lg:flex lg:col-span-5 flex-col bg-white border-r border-slate-200">
    <div className="relative flex-1 overflow-hidden">
      <img src={IMG.community} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover"/>
    </div>
    <div className="p-10 bg-white">
      <h2 className="font-serif text-3xl text-slate-900 leading-tight">{heading}</h2>
      <div className="cod-rule-red mt-4"/>
      <p className="text-slate-600 mt-4 leading-relaxed">{sub}</p>
    </div>
  </div>
);

export function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name}`);
      // First-login password reset (admin-created staff / partner accounts)
      if (u.must_reset_password) { navigate("/reset-password"); return; }
      navigate(portalForUser(u));
    }
    catch (e) { toast.error(e.response?.data?.detail || "Sign in failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-white" data-testid="signin-page">
      <Seo title="Sign In" description="Sign in to Chicago Opportunity Desk to continue your Opportunity Plan, upload documents, message your Case Manager, and pick up right where you left off." path="/signin" noindex
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Sign In", path: "/signin" }]}
        faq={[
          { q: "Forgot your password?", a: "Email info@joinCOD.org or call (312) 343-4183. A COD team member will help you reset it during business hours." },
          { q: "Never received a welcome email?", a: "Check spam or promotional folders for a message from info@joinCOD.org. If it's not there, contact us and we&#39;ll resend it." },
          { q: "Have an account with a different email?", a: "Sign in with the exact email you used when you first spoke with the Opportunity Navigator. Do not create a second account — we can merge duplicates on request." },
        ]}
      />
      <div className="cod-stripe"/>
      <div className="grid lg:grid-cols-12 min-h-[calc(100vh-4px)]">
        <SidePanel heading="Welcome back to COD." sub="Sign in to continue your plan, message your Case Manager, and pick up where you left off."/>
        <div className="lg:col-span-7 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <Link to="/" className="inline-block mb-8 lg:hidden" aria-label="Chicago Opportunity Desk — Home"><CODLogoMark size={48} onLight/></Link>
            <p className="cod-eyebrow">Sign in</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2">Continue your journey.</h1>
            <div className="cod-rule-red mt-4 mb-6"/>
            <p className="text-sm text-slate-600 mb-6">Chicago Opportunity Desk keeps your progress in one place — your Opportunity Plan, tasks, documents, appointments, and messages with your Case Manager. Sign in with the email you used to create your account.</p>
            <form onSubmit={submit} className="space-y-5">
              <div><Label htmlFor="email" className="text-slate-700">Email</Label><Input id="email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="signin-email"/></div>
              <div><Label htmlFor="pw" className="text-slate-700">Password</Label><Input id="pw" type="password" required value={password} onChange={e=>setPw(e.target.value)} className="mt-1.5 h-11" data-testid="signin-password"/></div>
              <Button type="submit" disabled={busy} className="w-full h-11 cod-bg-red" data-testid="signin-submit">{busy?"Signing in...":"Sign In"}</Button>
            </form>
            <p className="mt-6 text-sm text-slate-600">New here? <Link to="/start" className="font-semibold cod-link text-[var(--cod-navy)]" data-testid="link-signup">Create an account</Link></p>

            {/* What you can do once signed in */}
            <div className="mt-8 border-t border-slate-200 pt-6" data-testid="signin-value-block">
              <p className="cod-eyebrow mb-3">What signing in unlocks</p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{background:"var(--cod-red)"}}/> Your active <strong className="text-slate-900">Opportunity Plan</strong> and every prior version.</li>
                <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{background:"var(--cod-red)"}}/> Secure <strong className="text-slate-900">document vault</strong> for resumes, IDs, credentials, and background clearance letters.</li>
                <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{background:"var(--cod-red)"}}/> <strong className="text-slate-900">Messages</strong> with your assigned COD Case Manager.</li>
                <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{background:"var(--cod-red)"}}/> Scheduled <strong className="text-slate-900">appointments</strong>, reminders, and one-click reschedule.</li>
                <li className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{background:"var(--cod-red)"}}/> Marketplace <strong className="text-slate-900">recommendations</strong> tailored to your goal.</li>
              </ul>
            </div>

            {/* FAQ */}
            <div className="mt-8 border-t border-slate-200 pt-6" data-testid="signin-faq">
              <p className="cod-eyebrow mb-3">Sign-in help</p>
              <div className="space-y-3 text-sm text-slate-700">
                <div>
                  <p className="font-semibold text-slate-900">Forgot your password?</p>
                  <p className="mt-1">Email <a href="mailto:info@joinCOD.org" className="cod-link text-[var(--cod-navy)] font-medium">info@joinCOD.org</a> or call <span className="whitespace-nowrap">(312) 343-4183</span>. A COD team member will help you reset it during business hours.</p>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Never received a welcome email?</p>
                  <p className="mt-1">Check spam or promotional folders for a message from <span className="whitespace-nowrap">info@joinCOD.org</span>. If it&#39;s not there, contact us and we&#39;ll resend it.</p>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Have an account with a different email?</p>
                  <p className="mt-1">Sign in with the exact email you used when you first spoke with the Opportunity Navigator. Do not create a second account — we can merge duplicates on request.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 text-xs text-slate-500 border-t border-slate-200 pt-4">
              <div className="font-semibold mb-1 text-slate-700">Demo accounts</div>
              <div>Participant: marcus@example.com / MarcusCOD!2026</div>
              <div>Staff: staff@cod.org / StaffCOD!2026</div>
              <div>Admin: admin@cod.org / AdminCOD!2026</div>
              <div>Partner: partner@cod.org / PartnerCOD!2026</div>
            </div>

            <p className="mt-6 text-xs text-slate-500">
              Not a participant? <Link to="/employers" className="cod-link text-[var(--cod-navy)] font-medium">Employers</Link> · <Link to="/partners" className="cod-link text-[var(--cod-navy)] font-medium">Partners</Link> · <Link to="/contact" className="cod-link text-[var(--cod-navy)] font-medium">Contact</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SignUp() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const intent = params.get("intent");                     // e.g. "find_work"
  const isTalentIntent = intent === "find_work";
  const [step, setStep] = useState(isTalentIntent ? 2 : 1); // skip role picker for talent flow
  const [form, setForm] = useState({
    email:"", password:"", full_name:"", role: isTalentIntent ? "participant" : "participant", phone:"",
    organization_name:"",
  });
  const [busy, setBusy] = useState(false);

  // If the visitor arrived from the public Navigator, pre-fill from the stored lead
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem("cod_public_nav_v2");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.lead) {
        setForm(f => ({
          ...f,
          email: f.email || parsed.lead.email || "",
          full_name: f.full_name || parsed.lead.name || "",
          phone: f.phone || parsed.lead.phone || "",
        }));
      }
    } catch { /* noop */ }
  }, []);

  const needsOrgName = ["employer","community_partner","training_provider",
                        "government_agency","funding_org"].includes(form.role);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const u = await signup(form);
      // After successful signup for a participant, transfer public-Navigator context (if any)
      if (u.role === "participant") {
        try {
          const raw = localStorage.getItem("cod_public_nav_v2");
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.session_id && Array.isArray(parsed.messages) && parsed.messages.length > 0) {
              const { navApi } = await import("../lib/api");
              await navApi.contextTransfer(parsed.session_id, parsed.messages, parsed.lead || null);
            }
          }
        } catch { /* transfer is best-effort; do not block signup */ }
      }
      toast.success("Welcome to COD", { icon: <CODLogoSVG size={22}/> });
      // Candidate Network intent → land on the Candidate Profile with a welcome banner.
      if (isTalentIntent && u.role === "participant") {
        navigate("/app/talent-profile?welcome=1");
      } else {
        navigate(portalForUser(u));
      }
    }
    catch (e) { toast.error(e.response?.data?.detail || "Sign up failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Get Started"
        description="Create your Chicago Opportunity Desk account. Choose the option that best describes your role — participant, employer, community partner, training provider, government agency, funding organization, or volunteer."
        path="/start"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Create Account", path: "/start" }]}
      />
      <div className="cod-stripe"/>
      <div className="grid lg:grid-cols-12 min-h-[calc(100vh-4px)]">
        <SidePanel heading="One platform. Every workspace." sub="Sign in once, land in the right portal. Your role shapes what you see and what you can do."/>
        <div className="lg:col-span-7 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-2xl">
            <Link to="/" className="inline-block mb-8 lg:hidden" aria-label="Chicago Opportunity Desk — Home"><CODLogoMark size={48} onLight/></Link>
            {step === 1 ? (
              <>
                <p className="cod-eyebrow">Create Your Account</p>
                <h1 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2">What best describes your position?</h1>
                <div className="cod-rule-red mt-4 mb-6"/>
                <p className="text-sm text-slate-600 mb-6">Please select the option that best describes your relationship with Chicago Opportunity Desk.</p>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="signup-role-picker">
                  {PUBLIC_SIGNUP_ROLES.map(r => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setForm({...form, role: r.value})}
                      data-testid={`role-option-${r.value}`}
                      className={`text-left rounded-lg border p-4 transition ${
                        form.role === r.value
                          ? "border-[var(--cod-red)] bg-red-50/50 ring-1 ring-[var(--cod-red)]"
                          : "border-slate-200 hover:border-slate-400 bg-white"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`mt-1 h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                          form.role === r.value
                            ? "border-[var(--cod-red)] bg-[var(--cod-red)]"
                            : "border-slate-300"
                        }`}/>
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{r.label}</div>
                          <div className="text-xs text-slate-600 mt-1 leading-relaxed">{r.desc}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 sm:col-span-2">
                    <div className="text-sm font-semibold text-slate-900">Staff Member <span className="text-slate-500 font-normal">(Invitation Required)</span></div>
                    <div className="text-xs text-slate-600 mt-1">Staff accounts are created by a COD administrator. Contact <a href="mailto:info@joinCOD.org" className="cod-link">info@joinCOD.org</a> to request access.</div>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-6 leading-relaxed">
                  Your selection determines your portal, dashboard, and available features. Additional roles and permissions may be assigned in the future by an authorized administrator.
                </p>
                <div className="mt-6 flex justify-end">
                  <Button
                    type="button"
                    onClick={() => {
                      // Employers and partner-family roles use the rich onboarding wizards
                      if (form.role === "employer") {
                        navigate("/onboarding/employer");
                        return;
                      }
                      const partnerTypes = ["community_partner","training_provider","government_agency","funding_org"];
                      if (partnerTypes.includes(form.role)) {
                        navigate(`/onboarding/partner?type=${form.role}`);
                        return;
                      }
                      setStep(2);
                    }}
                    className="h-11 px-8 cod-bg-red"
                    data-testid="signup-role-continue"
                  >
                    Continue
                  </Button>
                </div>
                <p className="mt-6 text-sm text-slate-600">Already here? <Link to="/signin" className="font-semibold cod-link text-[var(--cod-navy)]" data-testid="link-signin">Sign in</Link></p>
              </>
            ) : (
              <>
                <p className="cod-eyebrow">{isTalentIntent ? "Candidate Network · Create Your Account" : "Create Your Account · Step 2 of 2"}</p>
                <h1 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2">{isTalentIntent ? "Join Chicago Opportunity Desk's Candidate Network." : "Your details."}</h1>
                <div className="cod-rule-red mt-4 mb-6"/>
                {isTalentIntent ? (
                  <p className="text-sm text-slate-600 mb-6">Create your free account. On the next screen you'll complete a short Candidate Profile so COD can review your skills and employment goals and consider you for matching opportunities.</p>
                ) : (
                  <p className="text-sm text-slate-600 mb-6">Signing up as <span className="font-semibold text-slate-900">{PUBLIC_SIGNUP_ROLES.find(r=>r.value===form.role)?.label}</span>. <button type="button" onClick={()=>setStep(1)} className="cod-link text-[var(--cod-navy)] font-medium" data-testid="signup-role-change">Change</button></p>
                )}
                <form onSubmit={submit} className="space-y-5">
                  <div><Label className="text-slate-700">Full Name</Label><Input required value={form.full_name} onChange={e=>setForm({...form, full_name:e.target.value})} className="mt-1.5 h-11" data-testid="signup-name"/></div>
                  <div><Label className="text-slate-700">Email</Label><Input type="email" required value={form.email} onChange={e=>setForm({...form, email:e.target.value})} className="mt-1.5 h-11" data-testid="signup-email"/></div>
                  {needsOrgName && (
                    <div>
                      <Label className="text-slate-700">Organization Name</Label>
                      <Input required value={form.organization_name} onChange={e=>setForm({...form, organization_name:e.target.value})} className="mt-1.5 h-11" data-testid="signup-org-name" placeholder="e.g., Cook County Health"/>
                      <p className="text-xs text-slate-500 mt-1">A profile will be created for your organization. You can add teammates and classifications later.</p>
                    </div>
                  )}
                  <div><Label className="text-slate-700">Password</Label><Input type="password" required minLength={6} value={form.password} onChange={e=>setForm({...form, password:e.target.value})} className="mt-1.5 h-11" data-testid="signup-password"/></div>
                  <div><Label className="text-slate-700">Phone (optional)</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} className="mt-1.5 h-11" data-testid="signup-phone"/></div>
                  <Button type="submit" disabled={busy} className="w-full h-11 cod-bg-red" data-testid="signup-submit">{busy?"Creating...":"Create Account"}</Button>
                </form>
                <p className="mt-6 text-sm text-slate-600">Already here? <Link to="/signin" className="font-semibold cod-link text-[var(--cod-navy)]" data-testid="link-signin">Sign in</Link></p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
